import { useMemo } from 'react';
import { ContactInfoType } from '@/customers/application/types/CustomerTypes';

export interface ContactOption {
  id: number | string;
  name: string;
  contactType?: string;
  contactValue?: string;
}

export interface ContactInfo {
  id?: number | null;
  type?: string | null;
  contact?: string | null;
}

export interface Contact {
  id?: number | null;
  firstName?: string | null;
  lastName?: string | null;
  contactInformation?: ContactInfo[] | null;
}

export interface UseContactOptionsProps {
  contacts?: Contact[] | null;
  rawCaller?: string | null;
  // Accept Set or array for convenience; we'll normalise internally
  allowedTypes?: Set<string> | readonly string[];
  formatName?: (contact: Contact, info: ContactInfo) => string;
}

export interface UseContactOptionsResult {
  options: ContactOption[];
  initialCallerId?: number | string;
}

/**
 * Default formatter for contact display name used by useContactOptions
 */
export function defaultFormatContactName(contact: Contact, info: ContactInfo): string {
  const contactName = `${contact.firstName || ''} ${contact.lastName || ''}`.trim() || 'Unknown Contact';
  return `${contactName} | ${info.contact ?? ''}`;
}

/**
 * Hook to transform contacts into select options with consistent formatting
 */
export function useContactOptions({
  contacts,
  rawCaller,
  allowedTypes = new Set([ContactInfoType.EMAIL, ContactInfoType.MOBILE, ContactInfoType.LANDLINE]),
  formatName = defaultFormatContactName
}: UseContactOptionsProps): UseContactOptionsResult {
  // Normalise allowedTypes to a stable key so passing a new Set each render
  // (e.g. new Set([...])) does not break memoisation.
  const allowedTypesArray = Array.isArray(allowedTypes) ? [...allowedTypes] : Array.from(allowedTypes);
  const allowedTypesKey = allowedTypesArray.slice().sort().join(',');

  return useMemo(() => {
    const options: ContactOption[] = [];
    const seenNames = new Set<string>();
    const typesArray = allowedTypesKey ? allowedTypesKey.split(',').filter(Boolean) : [];
    const allowedTypesSet = new Set(typesArray);

    const normalize = (value?: string | null) => (value ?? '').replace(/\s+/g, ' ').trim();

    // Add contact options if they exist
    if (contacts && contacts.length > 0) {
      const mappedContacts = contacts.flatMap((contact) => {
        const contactInfos = contact.contactInformation ?? [];
        return contactInfos
          .filter((info) => info?.type && allowedTypesSet.has(info.type))
          .map((info) => {
            const contactName = formatName(contact, info);

            return {
              id: info.id ?? `${contact.id}-${info.type}`,
              name: contactName,
              contactType: info.type ?? '',
              contactValue: info.contact ?? ''
            };
          });
      });

      // Deduplicate contacts by ID and track names
      const dedupedContacts = Array.from(new Map(mappedContacts.map((entry) => [entry.id, entry])).values());

      dedupedContacts.forEach((contact) => {
        seenNames.add(normalize(contact.name));
        options.push(contact);
      });
    }

    // Add caller option only if it's not already in the contacts
    const rawCallerNormalized = normalize(rawCaller);
    if (rawCallerNormalized && !seenNames.has(rawCallerNormalized)) {
      options.unshift({
        id: 0,
        name: rawCallerNormalized
      });
    }

    // Find initial caller ID if rawCaller exists
    let initialCallerId: number | string | undefined;
    if (rawCallerNormalized) {
      const callerOption = options.find((option) => normalize(option.name) === rawCallerNormalized);
      if (callerOption) {
        initialCallerId = callerOption.id;
      }
    }

    return {
      options,
      initialCallerId
    };
  }, [contacts, rawCaller, allowedTypesKey, formatName]);
}
